import { A11yDropdownItemProps } from '../../types/a11y.types'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { FC, MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { LeftNavDropdownMenuItem, PlatformNavIcons } from './LeftNav.types'
import { LinkOrActionWrapper } from '../layout/Utils'
import { Next, NextFlipped, theme } from '@dtx-company/shared-components/src'
import { PlatformNavIcon } from './PlatformNavIcon'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text, TruncateText } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { UnstyledButton } from '../layout/header/v2/styles'
import { m as motion } from 'framer-motion'
import { rightArrowBlue } from '@app/common/src/constants/assets'
import { setNavLeftMenu } from '@dtx-company/inter-app/src/redux/slices/config-slice'
import { useDispatch } from 'react-redux'
import { useIsMobile } from '@dtx-company/true-common/src/hooks/useIsMobile'
import { useRouter } from 'next/router'
import Link from 'next/link'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'
import useDropdownMenu from '../../utils/useAccessibleDropdownMenu'

const dropdownItemHidden = 'dropdownItemHidden'
const dropdownItemShown = 'dropdownItemShown'
const dropdownItemVariants = {
  [dropdownItemHidden]: {
    height: '0px',
    y: -20,
    opacity: 0,
    transition: { ease: 'easeOut', duration: 0.25 }
  },
  [dropdownItemShown]: {
    height: '100%',
    y: 0,
    opacity: 1,
    transition: { duration: 0.25 }
  }
}

interface LeftNavDropdownMenuProps {
  header: string
  iconSrc?: string
  iconId?: PlatformNavIcons
  items?: LeftNavDropdownMenuItem[]
  testId?: string
  itemProps?: A11yDropdownItemProps[]
  onClick?: () => void
  open?: boolean
  headerTag?: FC
  topItem?: JSX.Element
}

const DropdownSubMenuItem: FC<{
  item: LeftNavDropdownMenuItem
  disabled: boolean
  a11yProps?: A11yDropdownItemProps
}> = ({ item, disabled, a11yProps }) => {
  const [hovered, setHovered] = useState(false)
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])
  const isMobile = useIsMobile()

  const highlightItem = Boolean(hovered || item.highlightDefault)

  const handleSetHovered = (nextHoveredState: boolean): void => {
    if (!disabled) setHovered(nextHoveredState)
  }

  const textColor = useMemo(() => {
    if (disabled) return ['secondary.disabled', 'secondary.disabled']
    if (highlightItem) return ['primary.flowBlue', 'primary.flowBlue']
    return ['primary.grey', 'primary.grey']
  }, [disabled, highlightItem])

  return (
    <Box
      onMouseEnter={() => handleSetHovered(true)}
      onMouseLeave={() => handleSetHovered(false)}
      width="max-content"
      py={['12px', '6px']}
      role="button"
      aria-disabled={disabled}
      onClick={() => {
        if (disabled) return
        events.Global_PlatformNav_Clicked_DropdownMenuItem(item.text)
        setMenuClosed()
      }}
      data-testid={`${item.text}-dropdown-submenu-item`}
    >
      <LinkOrActionWrapper item={item} a11yProps={isMobile ? undefined : a11yProps}>
        <Box width="100%" cursor={!disabled ? 'pointer' : 'default'} title={item.titleText}>
          <Box width="32px" height="100%" justifyContent="flex-start">
            {highlightItem && (
              <Icon src={rightArrowBlue} width="12px" height="12px" mt={['4px', '4px', '0px']} />
            )}
          </Box>
          <TruncateText
            variant={['body/large', 'body/large', 'body/small']}
            maxWidth={['75vw', '75vw', '90px']}
            color={textColor}
            tooltip={(disabled && item.tooltipText) || undefined}
            tooltipProps={{
              top: '-5px',
              left: '-20px',
              bg: theme.colors.primary.black,
              color: theme.colors.primary.white
            }}
          >
            {item.text}
          </TruncateText>
        </Box>
      </LinkOrActionWrapper>
    </Box>
  )
}

const DropdownSubMenu: FC<{
  open: boolean
  items?: LeftNavDropdownMenuItem[]
  itemProps?: A11yDropdownItemProps[]
  topItem?: JSX.Element
}> = props => (
  <Box flexDirection="column" style={{ pointerEvents: props.open ? 'auto' : 'none' }}>
    <motion.div
      variants={dropdownItemVariants}
      animate={props.open ? dropdownItemShown : dropdownItemHidden}
      initial={dropdownItemHidden}
      style={{
        flexDirection: 'column'
      }}
    >
      <Box flexDirection="column" p="8px">
        {props.topItem}
        {props.items?.map((i, idx) => (
          <DropdownSubMenuItem
            disabled={!props.open || !!i.disabled}
            item={i}
            key={`${i.text}-${idx}`}
            a11yProps={props.itemProps?.[idx]}
          />
        ))}
      </Box>
    </motion.div>
  </Box>
)

export const LeftNavDropdownMenu: FC<LeftNavDropdownMenuProps> = props => {
  const [hovered, setHovered] = useState(false)
  const isMobile = useIsMobile()
  const { buttonProps, itemProps, isOpen, setIsOpen } = useDropdownMenu(
    props.items?.length || 0,
    {
      buttonCallback: () => events.Global_PlatformNav_Clicked_DropdownMenu(props.header)
    },
    props.open
  )

  const backgroundColor = useMemo(() => {
    if (isOpen || props.open) return 'secondary.backgroundActive'
    else if (hovered) return 'secondary.backgroundDark'
    else return ['primary.white', 'primary.white', 'secondary.backgroundLight']
  }, [props.open, isOpen, hovered])

  const textColor = useMemo(() => {
    return isOpen || props.open
      ? ['primary.flowBlue', 'primary.flowBlue']
      : ['primary.black', 'primary.black']
  }, [props.open, isOpen])

  const iconColor = useMemo(() => {
    return isOpen || props.open ? theme.colors.primary.flowBlue : theme.colors.primary.black
  }, [props.open, isOpen])

  useEffect(() => {
    if (props?.open) setIsOpen(true)
  }, [props?.open, setIsOpen])

  return (
    <Box flexDirection="column" width="100%">
      <Box
        backgroundColor={backgroundColor}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        borderRadius="4px"
        height={['52px', '52px', '30px']}
        width="100%"
        px="8px"
      >
        <UnstyledButton
          aria-label={props.header}
          aria-labelledby="open-nav-dropdown"
          data-testid={props.testId}
          {...buttonProps}
        >
          {props.iconSrc && (
            <Icon
              src={props.iconSrc}
              width={['30px', '30px', '20px']}
              height={['30px', '30px', '20px']}
            />
          )}
          {props.iconId && (
            <PlatformNavIcon
              iconId={props.iconId}
              color={iconColor}
              fontSize={isMobile ? 32 : 20}
            />
          )}
          <Spacer mr={['16px', '16px', '12px']} />
          <Text
            fontWeight={['600', '600', '600']}
            variant={['body/large', 'body/medium', 'body/medium']}
            color={textColor}
            my="auto"
            onClick={() => {
              if (props?.onClick) props.onClick()
            }}
          >
            {props.header}
          </Text>
          <Spacer mr={['16px', '16px', '12px']} />
          {props.headerTag && <props.headerTag />}
          <Box rotation={90} height="100%" mt="auto">
            {isOpen ? (
              <NextFlipped color={iconColor} fontSize={isMobile ? 16 : 12} />
            ) : (
              <Next color={iconColor} fontSize={isMobile ? 16 : 12} />
            )}
          </Box>
        </UnstyledButton>
      </Box>
      <DropdownSubMenu
        open={isOpen}
        items={props.items}
        itemProps={itemProps}
        topItem={props.topItem}
      />
    </Box>
  )
}

interface LeftNavLinkProps {
  link: string
  text: string
  icon?: string
  iconId?: PlatformNavIcons
  testId?: string
  disabled?: boolean
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void
  children?: ReactNode
}

export const LeftNavLink: FC<LeftNavLinkProps> = props => {
  const [hovered, setHovered] = useState(false)
  const router = useRouter()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])
  const isActiveNavLink = router.pathname === props.link
  const backgroundColor = useMemo(() => {
    if (isActiveNavLink) return 'secondary.backgroundActive'
    else if (hovered) return 'secondary.backgroundDark'
    else return ['primary.white', 'primary.white', 'secondary.backgroundLight']
  }, [isActiveNavLink, hovered])

  const onClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (props.onClick) props.onClick(e)
    setMenuClosed()
    events.Global_PlatformNav_Clicked_NavLink(props.text)
  }
  return (
    <Box
      backgroundColor={backgroundColor}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      borderRadius="4px"
      height={['52px', '52px', '30px']}
      px="8px"
      data-testid={props.testId}
      width="100%"
    >
      <Link
        href={props.link}
        tabIndex={0}
        style={{ margin: 'auto 0', width: '100%' }}
        onClick={e => {
          if (props.disabled) e.preventDefault()
        }}
      >
        <Box width="100%" my="auto" cursor="pointer">
          {/* TODO: Change icon color to blue on hover  */}
          {props.icon && <Box width="32px">{props.icon && <Icon src={props.icon} />}</Box>}
          {props.iconId && (
            <Box width="32px">
              <PlatformNavIcon
                iconId={props.iconId}
                color={isActiveNavLink ? theme.colors.primary.flowBlue : theme.colors.primary.black}
                fontSize={isMobile ? 32 : 20}
              />
            </Box>
          )}
          {isMobile && <Spacer mr="16px" />}
          <Text
            variant={['body/large', 'body/medium', 'body/medium']}
            my="auto"
            fontWeight={['600', '600', '600']}
            style={{ whiteSpace: 'nowrap' }}
            color={
              isActiveNavLink
                ? ['primary.flowBlue', 'primary.flowBlue']
                : ['primary.black', 'primary.black']
            }
          >
            {props.text}
          </Text>
          {props.children}
        </Box>
      </Link>
    </Box>
  )
}
